import React, { useEffect } from 'react';
import MenuDropdown from '@app/components/common/MenuDropdown/MenuDropdown';
import { MenuItem } from '@mui/material';
import { useRouter } from 'next/router';
import { useHandleLogoutMutation } from '@app/store/login/api';
import ProfileImage from '@app/components/auth/profile-image';
import { useBreakpoint } from '@app/lib/hooks/use-breakpoint';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { initialAuthState, selectAuth, setAuth } from '@app/store/auth/authSlice';
import { AuthProps } from '@app/store/auth/types';
import { Logout } from '@app/components/icons/logout-icon';
import { statusErrorMessageHandler } from '@app/utils/errorUtils';
import { capitalize, capitalizeAllFirstAlphabets } from '@app/utils/stringUtils';
import { selectActiveOrganization } from '@app/store/active/slice';
import { useTranslation } from 'next-i18next';
import { useModal } from '../modal-views/context';
import RenameIcon from '../icons/rename-icon';
import MemberIcon from '../icons/member-icon';
import { useLazyGetBillingPortalQuery } from '@app/store/stripe/api';
import FormIcon from '@app/components/icons/form-icon';
import PricingIcon from '@app/components/icons/pricing-icon';
import environments from '@app/configs/environments';
import { OrganizationDto } from '@app/models/dtos/organization-dtos';
import { useLazyHasPendingInvitationsQuery } from '@app/store/invitation/api';

function ProfileDropdown({ isRoleVisible = true }: { isRoleVisible?: boolean }) {
    const router = useRouter();
    const screenSize = useBreakpoint();
    const { t } = useTranslation(['common', 'invitation']);
    const auth = useAppSelector(selectAuth);
    const activeOrganization = useAppSelector(selectActiveOrganization);
    const [logout, logoutResponse] = useHandleLogoutMutation();
    const [getPendingInvitations, { data: hasPendingInvitation, isSuccess }] = useLazyHasPendingInvitationsQuery();

    useEffect(() => {
        getPendingInvitations();
    }, []);

    const [trigger] = useLazyGetBillingPortalQuery();
    const { openModal } = useModal();
    const user: AuthProps = auth ?? null;
    const dispatch = useAppDispatch();

    const isLargeScreen = ['xs', '2xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'].indexOf(screenSize) === -1;

    if (logoutResponse.isError) {
        // @ts-ignore
        statusErrorMessageHandler(logoutResponse.error.originalStatus || logoutResponse.error.status, t);
    }

    const handlePricing = async () => {
        await router.push(`/${activeOrganization?.id}/pricing`);
    };

    const handleLogout = async () => {
        await logout();
    };

    const handleRenameUserName = async () => {
        openModal('CHANGE_USERNAME_MODAL', {
            name: user.name,
            email: user.email,
            userId: user.userId,
            onSuccess: (newName: string) => {
                dispatch(setAuth({ ...auth, name: newName }));
            }
        });
    };

    const handleReceipt = async () => {
        if (activeOrganization?.id) {
            const response = await trigger(activeOrganization?.id);
            if (response.data?.stripeCustomerPortalUrl) await router.push(response.data?.stripeCustomerPortalUrl);
        }
    };

    const getPendingInvitationsText = () => {
        return (
            <div className="relative inline-flex items-center">
                <span>{t('invitations', { ns: 'invitations' })}</span>
                {hasPendingInvitation && <span className="absolute -right-2 -top-1 h-2 w-2 rounded-full bg-red-500"></span>}
            </div>
        );
    };

    const items = [
        {
            icons: PricingIcon,
            text: t('pricing'),
            handleButton: handlePricing,
            id: 'pricing'
        },
        {
            icons: RenameIcon,
            text: t('updateUsername'),
            handleButton: handleRenameUserName,
            id: 'updateUserName'
        },
        {
            icons: MemberIcon,
            text: t('pendingInvitations'),
            handleButton: () => router.push('/invitations'),
            id: 'pendingInvitations'
        },
        {
            icons: Logout,
            text: t('logout'),
            handleButton: handleLogout,
            id: 'logout'
        }
    ];

    if (environments?.IS_SINGLE_TENANT) {
        items.splice(0, 1);
    }

    // Remove pending invitations if none
    if (isSuccess && !hasPendingInvitation) {
        items.splice(1, 1);
    }

    if (!environments?.IS_SINGLE_TENANT && !!activeOrganization?.customerId) {
        items.splice(1, 0, {
            icons: FormIcon,
            text: t('receipt'),
            handleButton: handleReceipt,
            id: 'receipt'
        });
    }

    useEffect(() => {
        if (logoutResponse.isSuccess) {
            dispatch(setAuth(initialAuthState));
            router.replace('/');
        }
         
    }, [logoutResponse.isSuccess]);

    const getRole = (activeOrganization: OrganizationDto) => {
        if (auth.isSuperAdmin) {
            return t('roleType.super_admin');
        } else if (auth.isSupportMember) {
            return t('roleType.support');
        }
        return activeOrganization?.userRole ? t(`roleType.${activeOrganization?.userRole}`) : '';
    };

    const DropdownItems = items.filter((item) => {
        if (item.id === 'logout' || item.id === 'updateUserName' || item.id === 'pendingInvitations') {
            return true;
        }
        return activeOrganization?.userRole === 'owner';
    });
    const imageSize = isLargeScreen ? 36 : 28;

    const newMenuContent = (
        <>
            <div style={{ position: 'relative', display: 'inline-block' }}>
                <ProfileImage
                    size={imageSize}
                    style={{
                        width: imageSize,
                        height: imageSize,
                        backgroundColor: '#E7D9FC',
                        color: '#7824ED'
                    }}
                    image={user?.picture}
                    name={user?.name ?? user?.email}
                />
                {hasPendingInvitation && (
                    <span
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 10,
                            height: 10,
                            backgroundColor: 'red',
                            borderRadius: '50%',
                            border: '2px solid white' // Optional: creates a border around the red dot
                        }}
                    />
                )}
            </div>{' '}
            {isLargeScreen && (
                <div className={'text-start'}>
                    <p className="h5 text-black-800">{capitalizeAllFirstAlphabets(user?.name?.trim() || user?.email || '')}</p>
                    {isRoleVisible && (
                        <p data-testid="organization-role" id={activeOrganization?.userRole} className="p4 text-black-600">
                            {capitalize(getRole(activeOrganization as OrganizationDto))}
                        </p>
                    )}
                </div>
            )}
        </>
    );
    if (user?.isLoading) return <div className="mt-1 h-12 w-9 animate-pulse rounded-[4px] bg-black-300 lg:w-[200px]" />;
    return (
        <MenuDropdown
            showExpandMore={true}
            width={200}
            onClick={(event: any) => {
                event.stopPropagation();
                event.preventDefault();
            }}
            PaperProps={{
                elevation: 0,
                sx: {
                    width: 200,
                    overflow: 'hidden',
                    borderRadius: 2,
                    filter: 'drop-shadow(0px 0px 15px rgba(0, 0, 0, 0.15))',
                    mt: 0.5,
                    padding: 0
                }
            }}
            id="profile-dropdown"
            menuTitle={''}
            menuContent={newMenuContent}
        >
            {DropdownItems.map((dropdownItem) => {
                return (
                    <MenuItem data-testid={dropdownItem.id} key={dropdownItem.text} className="p2 flex gap-2 py-2 text-black-1" onClick={dropdownItem.handleButton}>
                        {React.createElement(dropdownItem.icons, { height: 24, width: 24 })}
                        {dropdownItem.id === 'pendingInvitations' ? getPendingInvitationsText() : <p>{dropdownItem.text}</p>}
                    </MenuItem>
                );
            })}
        </MenuDropdown>
    );
}

export default ProfileDropdown;
